<script setup>
import {defineProps} from 'vue'
import slugify from 'slugify'

const props = defineProps({
  title: String,
  anchor: String,
  deprecated: {
    type: Boolean,
    default: false
  }
});

</script>


<template>
<section :id="props.anchor || slugify(props.title)" class="doc-section">
  <h2 class="section-heading">
    {{ props.title }}
    <span v-if="props.deprecated" class="badge-deprecated ms-2">DEPRECATED</span>
  </h2>
  <div class="section-grid">
    <div class="left-side">
      <slot name="left"></slot>
    </div>
    <div class="right-side">
      <div class="right-sticky">
        <slot name="right"></slot>
      </div>
    </div>
  </div>
</section>
</template>

<style scoped>
.doc-section {
  scroll-margin-top: 1.5rem;
  background: var(--surface-card);
  border-radius: var(--radius-xl);
  padding: 2rem;
}

.section-heading {
  font-family: var(--font-heading);
  color: var(--text-heading);
  font-size: 1.375rem;
  font-weight: 600;
  line-height: var(--lh-tight);
  margin: 0 0 2rem;
}

.section-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
  gap: 3rem;
  align-items: start;
}

/* Consistent vertical rhythm inside each column, independent of the
   utility classes authored per section. */
.left-side {
  color: var(--text-secondary);
  line-height: var(--lh-relaxed);
}

.left-side :deep(p) {
  margin-bottom: 1rem;
}

.left-side :deep(h4) {
  color: var(--text-heading);
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1.25rem;
}

.left-side :deep(code),
.left-side :deep(strong) {
  font-family: var(--font-heading);
  font-size: 0.85em;
  font-weight: 600;
  color: var(--text-heading);
  background: var(--surface-sunken);
  padding: 0.1rem 0.4rem;
  border-radius: var(--radius-xs);
}

.left-side :deep(a) {
  color: var(--accent-text);
  text-decoration: none;
}

.left-side :deep(a:hover) {
  text-decoration: underline;
}

.left-side :deep(ul) {
  padding-left: 1.25rem;
}

.left-side :deep(li) {
  margin-bottom: 0.5rem;
}

.right-sticky {
  position: sticky;
  top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Blocks in the right column carry their own utility margins; normalise them
   into the flex gap so spacing stays even. */
.right-sticky :deep(.block) {
  margin-bottom: 0 !important;
}

@media (max-width: 991px) {
  .section-grid {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .right-sticky {
    position: static;
  }
}

.badge-deprecated {
  background-color: var(--color-error);
  color: var(--text-heading);
  padding: 0.25rem 0.75rem;
  border-radius: var(--radius-pill);
  font-size: 0.75rem;
  font-weight: 600;
  vertical-align: middle;
}
</style>
